import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import LoadingScreen from '../components/LoadingScreen';

const LoadingContext = createContext();

export const useLoading = () => {
  return useContext(LoadingContext);
};

export const LoadingProvider = ({ children }) => {
  // Start hidden only if the document hasn't finished loading yet
  const [isLoading, setIsLoading] = useState(document.readyState !== 'complete');
  const [initialLoad, setInitialLoad] = useState(document.readyState !== 'complete');

  useEffect(() => {
    if (!initialLoad) return;

    const hideLoadingScreen = () => {
      setIsLoading(false);
      setInitialLoad(false);
    };

    // Page already loaded — show content immediately
    if (document.readyState === 'complete') {
      hideLoadingScreen();
      return;
    }

    // Page still loading — wait for it, with a 3s safety fallback
    const onLoad = () => hideLoadingScreen();
    window.addEventListener('load', onLoad);

    const fallback = setTimeout(() => {
      hideLoadingScreen();
    }, 3000);

    return () => {
      window.removeEventListener('load', onLoad);
      clearTimeout(fallback);
    };
  }, [initialLoad]);

  // Function to show loading during navigation
  const showLoading = () => setIsLoading(true);
  const hideLoading = () => setIsLoading(false);

  return (
    <LoadingContext.Provider value={{ isLoading, showLoading, hideLoading }}>
      {isLoading && <LoadingScreen />}
      {/* Use display:none instead of visibility:hidden to avoid layout/paint of hidden tree */}
      <div style={isLoading ? { display: 'none' } : undefined}>
        {children}
      </div>
    </LoadingContext.Provider>
  );
};

// Component to handle route changes - must be inside Router
export const RouteChangeHandler = () => {
  const { showLoading, hideLoading } = useLoading();
  const location = useLocation();
  const prevLocationRef = useRef(location.pathname);
  const scrollPositions = useRef({});
  const navigationTypeRef = useRef('push');

  useEffect(() => {
    const handlePopState = () => { navigationTypeRef.current = 'pop'; };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const fullPath = location.pathname + location.search;
    if (prevLocationRef.current !== fullPath) {
      const prevPath = prevLocationRef.current;
      const isBack = navigationTypeRef.current === 'pop';

      if (!isBack) {
        scrollPositions.current[prevPath] = window.scrollY;
      }

      prevLocationRef.current = fullPath;
      navigationTypeRef.current = 'push';

      if (isBack) {
        const saved = scrollPositions.current[fullPath];
        if (saved != null) {
          requestAnimationFrame(() => {
            setTimeout(() => window.scrollTo(0, saved), 50);
          });
        }
      } else {
        window.scrollTo(0, 0);
      }
    }
  }, [location.pathname, location.search, showLoading, hideLoading]);

  return null;
};